import { TONE_CHOICES } from '@/common/app/modules/media/articleCreator'
import { WidgetChoice } from '@/common/form/choice'
import { WidgetBadgeChoice } from '@/common/form/choice'
import { ArticleStatus } from '@/common/model/media/article'
import { ArticleCreationSource } from '@/common/model/media/article'

import { BadgeType } from '@/components/view/atoms/badge/Badge'

// 入力上限（文字数カウンタの分母）。「それ以上入力させない境界」を表す
export const ARTICLE_TITLE_MAX_LENGTH = 50
export const ARTICLE_SLUG_MAX_LENGTH = 50

// W2 の特集ページのファイル名（＝スラッグ）の拡張子（openspec: feature-page-article-slug）。
// .aspx はファイル名を変えられず、.liquid は拡張子を除いたファイル名だけを変えられる
// （backend/services/article_service.py の SLUG_EXT_* と対応）
export const ARTICLE_SLUG_EXT_FIXED = '.aspx'
export const ARTICLE_SLUG_EXT_STEM_EDITABLE = '.liquid'

export const ArticleSlugKind = {
  FIXED: 'fixed', // 変更できない（.aspx）
  STEM_EDITABLE: 'stemEditable', // 拡張子を除いたファイル名だけ変更できる（.liquid）
  FREE: 'free' // 自由に変更できる
} as const
export type ArticleSlugKind = (typeof ArticleSlugKind)[keyof typeof ArticleSlugKind]

// スラッグの拡張子から、編集できる範囲を決める。拡張子の大文字・小文字は区別しない
export const articleSlugKind = (slug: string | null | undefined): ArticleSlugKind => {
  const s = (slug ?? '').trim().toLowerCase()
  if (s.endsWith(ARTICLE_SLUG_EXT_FIXED)) return ArticleSlugKind.FIXED
  if (s.endsWith(ARTICLE_SLUG_EXT_STEM_EDITABLE)) return ArticleSlugKind.STEM_EDITABLE
  return ArticleSlugKind.FREE
}

// SEO 推奨文字数（全角換算）。入力上限とは別概念で、「検索結果で省略されない範囲」を表す。
// AI記事生成・AI診断・記事診断結果一覧が共通の基準として用いる
// （backend/services/article_diagnosis_service.py の TITLE_RECOMMENDED_* と対応）。
export const ARTICLE_TITLE_RECOMMENDED_MIN = 28
export const ARTICLE_TITLE_RECOMMENDED_MAX = 35
export const ARTICLE_META_DESCRIPTION_RECOMMENDED_MIN = 70
export const ARTICLE_META_DESCRIPTION_RECOMMENDED_MAX = 120

// -----------------------------------------------
// 記事ステータス（articles.status）
// -----------------------------------------------

export const articleStatusLabel = (status: string | null | undefined): string => {
  switch (status) {
    case 'draft':
      return '下書き'
    case 'pending_approval':
      return '承認依頼中'
    case 'uploaded':
      return 'アップロード済'
    case 'approved':
      return '公開'
    case 'private':
      return '非公開'
    default:
      return status ?? '-'
  }
}

export const articleStatusBadgeType = (status: string | null | undefined): BadgeType => {
  switch (status) {
    case 'draft':
      return BadgeType.DEFAULT
    case 'pending_approval':
      return BadgeType.CAUTION
    case 'uploaded':
      return BadgeType.SUCCESS
    case 'approved':
      return BadgeType.EMPHASIS
    case 'private':
      return BadgeType.SECONDARY
    default:
      return BadgeType.DEFAULT
  }
}

// 記事一覧・記事詳細の検索/編集用ステータス選択肢。
// 記事アップロード権限 OFF 時は「承認依頼中」「アップロード済」を除外する（§11.2.1）。
export const articleStatusChoices = (enableArticleUpload: boolean): WidgetBadgeChoice<string>[] => {
  const all: WidgetBadgeChoice<string>[] = Object.values(ArticleStatus).map((s) => ({
    value: s,
    name: articleStatusLabel(s),
    badgeType: articleStatusBadgeType(s)
  }))
  if (enableArticleUpload) return all
  return all.filter(
    (c) => c.value !== ArticleStatus.PENDING_APPROVAL && c.value !== ArticleStatus.UPLOADED
  )
}

// -----------------------------------------------
// 作成種別（articles.creation_source）
// -----------------------------------------------

export const articleCreationSourceLabel = (
  source: ArticleCreationSource | null | undefined
): string => {
  switch (source) {
    case 'new':
      return '記事作成'
    case 'edit':
      return '編集'
    case 'master_sync':
      return 'マスタ連携'
    default:
      return source ?? '-'
  }
}

export const articleCreationSourceBadgeType = (
  source: ArticleCreationSource | null | undefined
): BadgeType => {
  switch (source) {
    case 'new':
      return BadgeType.SQUARE_PRIMARY
    case 'edit':
      return BadgeType.SQUARE_SECONDARY
    case 'master_sync':
      return BadgeType.SQUARE_TERTIARY
    default:
      return BadgeType.SQUARE_SECONDARY
  }
}

export const ARTICLE_CREATION_SOURCE_CHOICES: WidgetChoice<ArticleCreationSource>[] = [
  { name: articleCreationSourceLabel('new'), value: 'new' },
  { name: articleCreationSourceLabel('edit'), value: 'edit' },
  { name: articleCreationSourceLabel('master_sync'), value: 'master_sync' }
]

// -----------------------------------------------
// AI記事構成案トーン（articles.tone）
// -----------------------------------------------

export const articleToneLabel = (tone: string | null | undefined): string => {
  switch (tone) {
    case 'professional':
      return '専門的'
    case 'casual':
      return 'カジュアル'
    case 'friendly':
      return 'フレンドリー'
    default:
      return tone ?? '-'
  }
}

// 記事作成・編集フォームのトーン選択肢（FormRadioButton用）
export const TONE_RADIO_CHOICES: WidgetChoice<string>[] = TONE_CHOICES.map((opt) => ({
  name: opt.label,
  value: opt.value,
  description: opt.description
}))

// 記事作成・編集フォームのペルソナ（想定される読み手）入力欄の記述例
export const PERSONA_PLACEHOLDER =
  '30代の共働き女性。時短できる家事グッズを探しているが、品質にもこだわりたい。'

// 記事作成・編集フォームの目標文字数選択肢（FormSelector用）
export const TARGET_CHAR_COUNT_MIN = 1000
export const TARGET_CHAR_COUNT_MAX = 5000
export const TARGET_CHAR_COUNT_STEP = 500

export const TARGET_CHAR_COUNT_CHOICES: WidgetChoice<number>[] = Array.from(
  { length: (TARGET_CHAR_COUNT_MAX - TARGET_CHAR_COUNT_MIN) / TARGET_CHAR_COUNT_STEP + 1 },
  (_, i) => TARGET_CHAR_COUNT_MIN + i * TARGET_CHAR_COUNT_STEP
).map((v) => ({ name: `${v.toLocaleString()}文字`, value: v }))

// -----------------------------------------------
// コンテンツブロック区分（article_parts.part_type）
// -----------------------------------------------

// product_list は content_html に {product_group_id, product_list_title, display_number} のJSONを保持する
export const articleProductListSummary = (contentHtml: string | null | undefined): string => {
  if (!contentHtml) return '未設定'
  try {
    const v = JSON.parse(contentHtml) as {
      product_group_id?: string
      product_list_title?: string
      display_number?: number
    }
    return `商品グループID: ${v.product_group_id ?? '-'} ／ タイトル: ${v.product_list_title ?? '-'} ／ 表示件数: ${v.display_number ?? '-'}`
  } catch {
    return contentHtml
  }
}

export const articlePartTypeLabel = (partType: string | null | undefined): string => {
  switch (partType) {
    case 'page_title':
      return 'ページタイトル'
    case 'header_banner_image':
      return 'ヘッダーバナー画像'
    case 'content_area_top':
      return 'コンテンツエリア上部'
    case 'product_list':
      return '商品一覧'
    case 'content_area_bottom':
      return 'コンテンツエリア下部'
    default:
      return partType ?? '-'
  }
}
